import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Grid, 
  TextField, 
  Button, 
  Switch, 
  FormControlLabel, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  Alert, 
  IconButton, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Backup as BackupIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Save as SaveIcon,
  RestartAlt as RestartAltIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: '4px',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  }
}));

const AdminSettings = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [backupDialogOpen, setBackupDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [successAlert, setSuccessAlert] = useState(false);

  // Mock settings data
  const [settings, setSettings] = useState({
    // General Settings
    siteName: 'গ্রামীণ বাজার',
    siteDescription: 'গ্রামীণ পণ্যের অনলাইন মার্কেটপ্লেস',
    contactEmail: 'admin@grameenbazar.com',
    contactPhone: '+880 1234 567890',
    address: 'ঢাকা, বাংলাদেশ',
    timezone: 'Asia/Dhaka',
    language: 'bn',
    currency: 'BDT',
    
    // Business Settings
    commissionRate: 5,
    taxRate: 15,
    freeShippingLimit: 1000,
    minOrderAmount: 100,
    maxOrderAmount: 50000,
    
    // Notification Settings
    emailNotifications: true,
    smsNotifications: true,
    pushNotifications: true,
    orderNotifications: true,
    lowStockAlerts: true,
    
    // Security Settings
    twoFactorAuth: false,
    passwordExpiry: 90,
    sessionTimeout: 30,
    maxLoginAttempts: 5,
    
    // Payment Settings
    bkashEnabled: true,
    nagadEnabled: true,
    rocketEnabled: false,
    cardPaymentEnabled: true,
    codEnabled: true,
  });

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSaveSettings = () => {
    // Here you would save settings to backend
    setSuccessAlert(true);
    setTimeout(() => setSuccessAlert(false), 3000);
  };

  const handleBackup = () => {
    setBackupDialogOpen(false);
    // Implement backup logic
    alert('ব্যাকআপ সফলভাবে শুরু হয়েছে!');
  };

  const handleSystemReset = () => {
    setResetDialogOpen(false);
    // Implement reset logic
    alert('সিস্টেম রিস্টার্ট সফলভাবে সম্পন্ন হয়েছে!');
  };

  const tabs = [
    { label: 'সাধারণ সেটিংস', icon: <SettingsIcon /> },
    { label: 'ব্যবসায়িক সেটিংস', icon: <BusinessIcon /> },
    { label: 'পেমেন্ট সেটিংস', icon: <PaymentIcon /> },
    { label: 'নোটিফিকেশন', icon: <NotificationsIcon /> },
    { label: 'নিরাপত্তা', icon: <SecurityIcon /> },
    { label: 'ব্যাকআপ ও রক্ষণাবেক্ষণ', icon: <BackupIcon /> },
  ];

  const recentBackups = [
    { id: 1, date: '২৫ মার্চ, ২০২৫', time: '১২:৩০ PM', size: '২.৪ GB', status: 'সম্পন্ন' },
    { id: 2, date: '২৪ মার্চ, ২০২৫', time: '১২:৩০ PM', size: '২.৩ GB', status: 'সম্পন্ন' },
    { id: 3, date: '২৩ মার্চ, ২০২৫', time: '১২:৩০ PM', size: '২.২ GB', status: 'সম্পন্ন' },
  ];

  const systemLogs = [
    { id: 1, type: 'তথ্য', message: 'নতুন ব্যবহারকারী নিবন্ধিত হয়েছে', time: '১০ মিনিট আগে' },
    { id: 2, type: 'সতর্কতা', message: 'উচ্চ সার্ভার লোড সনাক্ত', time: '৩০ মিনিট আগে' },
    { id: 3, type: 'ত্রুটি', message: 'পেমেন্ট গেটওয়ে সংযোগ ব্যর্থ', time: '১ ঘন্টা আগে' },
    { id: 4, type: 'তথ্য', message: 'স্বয়ংক্রিয় ব্যাকআপ সম্পন্ন', time: '২ ঘন্টা আগে' },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        সিস্টেম সেটিংস
      </Typography>

      {successAlert && (
        <Alert severity="success" sx={{ mb: 3 }}>
          সেটিংস সফলভাবে সংরক্ষিত হয়েছে!
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 72,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500
              }
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={index}
                label={tab.label} 
                icon={tab.icon}
                iconPosition="start"
                sx={{ minWidth: 160 }}
              />
            ))}
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* General Settings Tab */}
          {currentTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="সাইট তথ্য" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="সাইটের নাম"
                          value={settings.siteName}
                          onChange={(e) => handleSettingChange('siteName', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="সাইটের বিবরণ"
                          multiline
                          rows={3}
                          value={settings.siteDescription}
                          onChange={(e) => handleSettingChange('siteDescription', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="যোগাযোগের ইমেইল"
                          type="email"
                          value={settings.contactEmail}
                          onChange={(e) => handleSettingChange('contactEmail', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="যোগাযোগের ফোন"
                          value={settings.contactPhone}
                          onChange={(e) => handleSettingChange('contactPhone', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="আঞ্চলিক সেটিংস" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>ভাষা</InputLabel>
                          <Select
                            value={settings.language}
                            label="ভাষা"
                            onChange={(e) => handleSettingChange('language', e.target.value)}
                          >
                            <MenuItem value="bn">বাংলা</MenuItem>
                            <MenuItem value="en">English</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>টাইমজোন</InputLabel>
                          <Select
                            value={settings.timezone}
                            label="টাইমজোন"
                            onChange={(e) => handleSettingChange('timezone', e.target.value)}
                          >
                            <MenuItem value="Asia/Dhaka">এশিয়া/ঢাকা</MenuItem>
                            <MenuItem value="Asia/Kolkata">এশিয়া/কলকাতা</MenuItem>
                            <MenuItem value="UTC">UTC</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>মুদ্রা</InputLabel>
                          <Select
                            value={settings.currency}
                            label="মুদ্রা"
                            onChange={(e) => handleSettingChange('currency', e.target.value)}
                          >
                            <MenuItem value="BDT">বাংলাদেশী টাকা (৳)</MenuItem>
                            <MenuItem value="USD">US Dollar ($)</MenuItem>
                            <MenuItem value="EUR">Euro (€)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="ঠিকানা"
                          multiline
                          rows={3}
                          value={settings.address}
                          onChange={(e) => handleSettingChange('address', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Business Settings Tab */}
          {currentTab === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="কমিশন ও ফি" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                      color: '#333',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="কমিশন হার (%)"
                          type="number"
                          value={settings.commissionRate}
                          onChange={(e) => handleSettingChange('commissionRate', e.target.value)}
                          InputProps={{ inputProps: { min: 0, max: 100 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="ট্যাক্স হার (%)"
                          type="number"
                          value={settings.taxRate}
                          onChange={(e) => handleSettingChange('taxRate', e.target.value)}
                          InputProps={{ inputProps: { min: 0, max: 100 } }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="ফ্রি শিপিং সীমা (৳)"
                          type="number"
                          value={settings.freeShippingLimit}
                          onChange={(e) => handleSettingChange('freeShippingLimit', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="অর্ডার সীমা" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                      color: '#333',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="সর্বনিম্ন অর্ডার পরিমাণ (৳)"
                          type="number"
                          value={settings.minOrderAmount}
                          onChange={(e) => handleSettingChange('minOrderAmount', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="সর্বোচ্চ অর্ডার পরিমাণ (৳)"
                          type="number"
                          value={settings.maxOrderAmount}
                          onChange={(e) => handleSettingChange('maxOrderAmount', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Payment Settings Tab */}
          {currentTab === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <StyledCard>
                  <CardHeader 
                    title="পেমেন্ট পদ্ধতি" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">বিকাশ</Typography>
                              <Switch
                                checked={settings.bkashEnabled}
                                onChange={(e) => handleSettingChange('bkashEnabled', e.target.checked)}
                              />
                            </Box>
                            <TextField
                              fullWidth
                              label="মার্চেন্ট নম্বর"
                              value="01712345678"
                              size="small"
                            />
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">নগদ</Typography>
                              <Switch
                                checked={settings.nagadEnabled}
                                onChange={(e) => handleSettingChange('nagadEnabled', e.target.checked)}
                              />
                            </Box>
                            <TextField
                              fullWidth
                              label="মার্চেন্ট নম্বর"
                              value="01798765432"
                              size="small"
                            />
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">রকেট</Typography>
                              <Switch
                                checked={settings.rocketEnabled}
                                onChange={(e) => handleSettingChange('rocketEnabled', e.target.checked)}
                              />
                            </Box>
                            <TextField
                              fullWidth
                              label="মার্চেন্ট নম্বর"
                              value=""
                              size="small"
                              disabled={!settings.rocketEnabled}
                            />
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">কার্ড পেমেন্ট</Typography>
                              <Switch
                                checked={settings.cardPaymentEnabled}
                                onChange={(e) => handleSettingChange('cardPaymentEnabled', e.target.checked)}
                              />
                            </Box>
                            <TextField
                              fullWidth
                              label="গেটওয়ে কী"
                              type="password"
                              value="••••••••••••"
                              size="small"
                            />
                          </CardContent>
                        </Card>
                      </Grid>

                      <Grid item xs={12} sm={6} md={4}>
                        <Card variant="outlined">
                          <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6">ক্যাশ অন ডেলিভারি</Typography>
                              <Switch
                                checked={settings.codEnabled}
                                onChange={(e) => handleSettingChange('codEnabled', e.target.checked)}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              ডেলিভারির সময় নগদ পেমেন্ট
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Notification Settings Tab */}
          {currentTab === 3 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="নোটিফিকেশন সেটিংস" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon />
                        </ListItemIcon>
                        <ListItemText primary="ইমেইল নোটিফিকেশন" secondary="ইমেইলের মাধ্যমে সব আপডেট পান" />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.emailNotifications}
                            onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemIcon>
                          <NotificationsIcon />
                        </ListItemIcon>
                        <ListItemText primary="SMS নোটিফিকেশন" secondary="গুরুত্বপূর্ণ আপডেটের SMS পান" />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.smsNotifications}
                            onChange={(e) => handleSettingChange('smsNotifications', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemIcon>
                          <NotificationsIcon />
                        </ListItemIcon>
                        <ListItemText primary="পুশ নোটিফিকেশন" secondary="ব্রাউজার নোটিফিকেশন সক্রিয় করুন" />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.pushNotifications}
                            onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemIcon>
                          <BusinessIcon />
                        </ListItemIcon>
                        <ListItemText primary="অর্ডার নোটিফিকেশন" secondary="নতুন অর্ডারের সাথে সাথে জানুন" />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.orderNotifications}
                            onChange={(e) => handleSettingChange('orderNotifications', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <Divider />
                      <ListItem>
                        <ListItemIcon>
                          <WarningIcon />
                        </ListItemIcon>
                        <ListItemText primary="স্টক কম থাকার সতর্কতা" secondary="স্টক কম হলে সতর্কতা পান" />
                        <ListItemSecondaryAction>
                          <Switch
                            checked={settings.lowStockAlerts}
                            onChange={(e) => handleSettingChange('lowStockAlerts', e.target.checked)}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Security Settings Tab */}
          {currentTab === 4 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="নিরাপত্তা নীতি" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
                      color: '#333',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={settings.twoFactorAuth}
                              onChange={(e) => handleSettingChange('twoFactorAuth', e.target.checked)}
                            />
                          }
                          label="দ্বি-ফ্যাক্টর অথেন্টিকেশন"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="পাসওয়ার্ড মেয়াদ (দিন)"
                          type="number"
                          value={settings.passwordExpiry}
                          onChange={(e) => handleSettingChange('passwordExpiry', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="সেশন টাইমআউট (মিনিট)"
                          type="number"
                          value={settings.sessionTimeout}
                          onChange={(e) => handleSettingChange('sessionTimeout', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="সর্বোচ্চ লগইন চেষ্টা"
                          type="number"
                          value={settings.maxLoginAttempts}
                          onChange={(e) => handleSettingChange('maxLoginAttempts', e.target.value)}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="সিস্টেম নিরাপত্তা" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #a8caba 0%, #5d4e75 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="SSL সার্টিফিকেট" 
                          secondary="সক্রিয় ও বৈধ"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="ফায়ারওয়াল" 
                          secondary="সুরক্ষিত ও সক্রিয়"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <WarningIcon color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary="ডাটাবেস এনক্রিপশন" 
                          secondary="আপডেট প্রয়োজন"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Backup & Maintenance Tab */}
          {currentTab === 5 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="ডাটাবেস ব্যাকআপ" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" gutterBottom>
                        শেষ ব্যাকআপ: ২৫ মার্চ, ২০২৫ - ১২:৩০ PM
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        পরবর্তী স্বয়ংক্রিয় ব্যাকআপ: আজ ১২:৩০ AM
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <Button
                        variant="contained"
                        startIcon={<BackupIcon />}
                        onClick={() => setBackupDialogOpen(true)}
                        sx={{ flex: 1 }}
                      >
                        এখনই ব্যাকআপ করুন
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        sx={{ flex: 1 }}
                      >
                        ব্যাকআপ ডাউনলোড
                      </Button>
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>তারিখ</TableCell>
                            <TableCell>সাইজ</TableCell>
                            <TableCell>স্ট্যাটাস</TableCell>
                            <TableCell>অ্যাকশন</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {recentBackups.map((backup) => (
                            <TableRow key={backup.id}>
                              <TableCell>{backup.date}</TableCell>
                              <TableCell>{backup.size}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={backup.status} 
                                  color="success" 
                                  size="small" 
                                />
                              </TableCell>
                              <TableCell>
                                <IconButton size="small">
                                  <DownloadIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </StyledCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <StyledCard>
                  <CardHeader 
                    title="সিস্টেম রক্ষণাবেক্ষণ" 
                    sx={{ 
                      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                      color: 'white',
                      '& .MuiCardHeader-title': { fontWeight: 600 }
                    }}
                  />
                  <CardContent>
                    <Box sx={{ mb: 3 }}>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<RestartAltIcon />}
                        fullWidth
                        sx={{ mb: 2 }}
                        onClick={() => setResetDialogOpen(true)}
                      >
                        সিস্টেম রিস্টার্ট
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<UploadIcon />}
                        fullWidth
                        sx={{ mb: 2 }}
                      >
                        সিস্টেম আপডেট
                      </Button>
                    </Box>

                    <Typography variant="h6" gutterBottom>সিস্টেম লগ</Typography>
                    <List dense>
                      {systemLogs.map((log) => (
                        <ListItem key={log.id}>
                          <ListItemIcon>
                            {log.type === 'ত্রুটি' ? 
                              <WarningIcon color="error" /> : 
                              log.type === 'সতর্কতা' ? 
                              <WarningIcon color="warning" /> : 
                              <CheckCircleIcon color="success" />
                            }
                          </ListItemIcon>
                          <ListItemText 
                            primary={log.message}
                            secondary={log.time}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          )}

          {/* Save Button */}
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveSettings}
              sx={{ 
                minWidth: 200,
                background: 'linear-gradient(45deg, #4e279d 30%, #1275cb 90%)'
              }}
            >
              সেটিংস সংরক্ষণ করুন
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Backup Confirmation Dialog */}
      <Dialog open={backupDialogOpen} onClose={() => setBackupDialogOpen(false)}>
        <DialogTitle>ব্যাকআপ নিশ্চিতকরণ</DialogTitle>
        <DialogContent>
          <Typography>
            আপনি কি নিশ্চিত যে আপনি এখনই একটি সম্পূর্ণ ডাটাবেস ব্যাকআপ নিতে চান?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBackupDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained" onClick={handleBackup}>
            ব্যাকআপ শুরু করুন
          </Button>
        </DialogActions>
      </Dialog>

      {/* System Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>সিস্টেম রিস্টার্ট নিশ্চিতকরণ</DialogTitle>
        <DialogContent>
          <Typography>
            সিস্টেম রিস্টার্ট করলে সব ব্যবহারকারী সাময়িকভাবে সংযোগ হারাবে। আপনি কি নিশ্চিত?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>বাতিল</Button>
          <Button variant="contained" color="warning" onClick={handleSystemReset}>
            রিস্টার্ট করুন
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminSettings;
